import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Dna,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  BarChart3,
  Clock,
  Eye,
  Play,
  Award,
  FileUp,
  Database,
  Brain,
  FileText,
  LineChart,
  Zap,
  Users,
  Microscope
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { geneSeqerTestimonials } from '../../shared/testimonialData';

const GeneSeqer: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Users,
      title: '	End-to-End RNA-Seq Workflow',
      description: 'Supports the full analytics journey from FASTQ upload, QC and alignment through DEG calling, ML modelling, NLP overlay and exportable plots.'
    },
    {
      icon: FileUp,
      title: 'R-Shiny, No-Code Interface',
      description: 'Provides an intuitive, point-and-click UI so biologists can run complex pipelines without writing scripts or managing compute jobs.'
    },
    {
      icon: Zap,
      title: '	Differential Expression & ML Insights',
      description: 'Identifies differentially expressed genes and applies machine learning to uncover patterns linked to phenotypes or treatment response.'
    },
    {
      icon: Database,
      title: '	NLP Literature Integration',
      description: 'Layers in natural language processing of the literature to highlight known pathways, gene associations and potential biomarkers.'
    },
    {
      icon: Brain,
      title: '	Interactive Visualisations & Reporting',
      description: 'Generates dynamic plots, heatmaps and dashboards that can be customised and exported directly into slide decks or reports.'
    },
    {
      icon: FileText,
      title: '	Project & Sample Management',
      description: ' Organises datasets, metadata and analysis runs by project, making it easier for teams to track decisions and reproduce results.'
    },
    {
      icon: Eye,
      title: '	Scalable Cloud Architecture',
      description: 'Runs on a cloud-native stack that scales from small academic labs to large pharma R&D organisations.'
    },
    {
      icon: Clock,
      title: '	Continuous Updates & Models: ',
      description: 'Analytics modules and models are regularly refreshed so teams benefit from the latest methods without rebuilding pipelines themselves.'
    }
  ];

  const analyticsFlow = [
    {
      step: '01',
      title: 'Data Upload',
      description: 'Upload RNA-seq FASTQ files through an intuitive interface with automatic validation and preprocessing.',
      icon: FileUp
    },
    {
      step: '02',
      title: 'QC & Alignment',
      description: 'Automated quality control checks and sequence alignment ensure data integrity and accuracy.',
      icon: CheckCircle
    },
    {
      step: '03',
      title: 'DEG Calling',
      description: 'Differential gene expression analysis identifies significant changes across experimental conditions.',
      icon: Microscope
    },
    {
      step: '04',
      title: 'ML & NLP Analysis',
      description: 'Machine learning phenotype prediction and NLP literature overlay provide comprehensive insights.',
      icon: Brain
    },
    {
      step: '05',
      title: 'Visualization & Export',
      description: 'Interactive dynamic plots and flexible export options for publication-ready results.',
      icon: LineChart
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Accelerated Discovery Timelines',
      description: 'Shortens the time from sequencing run to interpretable result, keeping projects moving and reducing iteration cycles.',
    },
    {
      icon: Users,
      title: '	Reduced Dependence on Scarce Bioinformatics Resources',
      description: 'Frees specialist teams to focus on complex study design and custom modelling instead of repetitive pipeline work.',
    },
    {
      icon: Award,
      title: '	Lower Analysis Cost per Project',
      description: 'Standardised, automated workflows reduce manual effort and help organisations make better use of compute budgets.',
    },
    {
      icon: Clock,
      title: '	More Reproducible, Comparable Results',
      description: 'Consistent pipelines and clear metadata make it easier to compare studies and build on earlier work.',
    },
    {
      icon: Eye,
      title: '	Better Communication Across Teams',
      description: 'Interactive visuals and human-readable reports help scientists, managers and external partners align on what the data is saying.',
    }
  ];

  const capabilities = [
    {
      title: 'Target Discovery & Mechanism Studies',
      description: 'Quickly explore transcriptional changes across conditions to identify candidate targets, pathways and mechanisms of action.'
    },
    {
      title: '	Biomarker & Signature Development',
      description: 'Use ML workflows to build and evaluate gene signatures associated with response, toxicity or disease progression.'
    },
    {
      title: '	Preclinical & Translational Research',
      description: 'Standardise RNA-seq analysis across studies and sites, making it easier to compare results and move findings closer to the clinic.'
    },
    {
      title: 'Academic & Core Facility Support',
      description: 'Empower non-bioinformaticians to analyse their own datasets, while cores maintain central control over validated pipelines.'
    },
    {
      title: '	Pharma & Biotech Portfolio Projects',
      description: 'Apply consistent, auditable analytics across multiple programmes, improving decision quality and speeding up go/no-go milestones.'
    }
  ];

  const researchAreas = [
    {
      title: 'Secure Cloud or Private Deployment',
      description: 'Supports deployment in secure cloud environments or within an organisation’s own infrastructure, depending on policy requirements.',
    },
    {
      title: 'Encryption in Transit & at Rest',
      description: 'Applies standard encryption to protect files and results as they move through the pipeline and when they are stored.',
    },
    {
      title: '	Fine-Grained Access Control',
      description: 'Role-based permissions ensure that only authorised users can view, modify or export specific projects and datasets.',
    },
    {
      title: 'Audit Logging & Traceability',
      description: 'Tracks analysis runs, parameter selections and result exports, supporting internal review, collaboration and reproducibility.',
    },
    {
      title: 'Configurable Retention & Governance: ',
      description: 'Lets institutions define how long raw and processed data are retained, aligning the platform with local governance and regulatory needs.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-10 h-10 bg-accent rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 0.7, 0],
              }}
              transition={{
                duration: 10,
                delay: i * 1,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-secondary neon-glow">
                  <Dna className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-accent uppercase tracking-wide">RNA-Seq Platform</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-accent to-secondary bg-clip-text text-transparent">GeneSeqer</span>
                  </h1>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed justify"
              >
                GeneSeqer is an R-Shiny machine-learning platform built specifically for molecular biologists and life-science researchers. Users upload raw FASTQ files and, through a guided workflow, move from quality control and alignment to differential expression, ML-based pattern discovery, NLP literature insights and interactive visualisations. The goal is simple: remove bioinformatics bottlenecks so project teams can spend more time asking scientific questions and less time wrestling with command-line tools.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-accent to-secondary px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-accent/25 transition-all duration-300"
                >
                  Start Your Analysis
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Platform Overview
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">10x</div>
                  <div className="text-sm text-muted-foreground">Faster Analysis</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">0</div>
                  <div className="text-sm text-muted-foreground">Coding Required</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">100%</div>
                  <div className="text-sm text-muted-foreground">Guided UI</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-gradient-to-r from-accent to-secondary opacity-20 rounded-full blur-3xl"
                />

                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Products/ai-Products/geneSeqer.png'
                    alt="GeneSeqer RNA-Seq Analysis Platform"
                    className="w-full h-96 object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-accent/20 rounded-lg backdrop-blur-sm">
                          <Dna className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-semibold">ML-Powered</p>
                          <p className="text-sm text-white/80">RNA-Seq Analysis</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-accent">
                    <Microscope className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">R-SHINY</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Analytics Flow Diagram Section */}
      <section className="relative py-15 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              GeneSeqer Analytics Flow
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              The analytics flow includes data upload, QC & alignment, DEG calling, ML phenotype prediction,
              NLP literature overlay, and dynamic plots & export. The guided UI ensures ease of use at every step.
            </p>
          </motion.div>

          {/* Flowchart Diagram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="glass-strong rounded-2xl p-6 sm:p-8 lg:p-12 overflow-hidden">
              {/* Desktop Flow - Horizontal */}
              <div className="hidden lg:block">
                <div className="flex items-start justify-between gap-4 max-w-6xl mx-auto">
                  {[
                    { num: '01', title: 'Data Input', desc: 'RNA-seq data upload and preprocessing', icon: FileUp },
                    { num: '02', title: 'DEG Analysis', desc: 'Differential gene expression processing', icon: Microscope },
                    { num: '03', title: 'ML Processing', desc: 'Machine learning pattern recognition', icon: Brain },
                    { num: '04', title: 'NLP Analysis', desc: 'Natural language processing of findings', icon: FileText },
                    { num: '05', title: 'Visualization', desc: 'Interactive data visualization outputs', icon: BarChart3 }
                  ].map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <React.Fragment key={step.num}>
                        {/* Flow Step */}
                        <motion.div
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.7, delay: index * 0.12 }}
                          viewport={{ once: true }}
                          className="flex-1 flex flex-col items-center"
                        >
                          {/* Circular Icon Container */}
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ duration: 0.3 }}
                            className="relative mb-6"
                          >
                            {/* Outer Glow Ring */}
                            <motion.div
                              animate={{
                                scale: [1, 1.15, 1],
                                opacity: [0.5, 0.8, 0.5]
                              }}
                              transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                delay: index * 0.2
                              }}
                              className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/40 to-secondary/40 blur-xl"
                            />

                            {/* Icon Circle */}
                            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-accent/10 to-secondary/10 backdrop-blur-sm border-2 border-accent/30 flex items-center justify-center group-hover:border-accent/60 transition-all duration-300">
                              <Icon className="w-9 h-9 text-accent" />
                            </div>

                            {/* Number Badge */}
                            <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center text-xs font-bold text-white shadow-lg neon-glow">
                              {step.num}
                            </div>
                          </motion.div>

                          {/* Title */}
                          <motion.h3
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: index * 0.12 + 0.2 }}
                            viewport={{ once: true }}
                            className="text-center font-semibold text-white mb-2"
                          >
                            {step.title}
                          </motion.h3>

                          {/* Description */}
                          <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: index * 0.12 + 0.3 }}
                            viewport={{ once: true }}
                            className="text-center text-sm text-muted-foreground leading-relaxed"
                          >
                            {step.desc}
                          </motion.p>
                        </motion.div>

                        {/* Arrow Connector */}
                        {index < 4 && (
                          <motion.div
                            initial={{ opacity: 0, scaleX: 0 }}
                            whileInView={{ opacity: 1, scaleX: 1 }}
                            transition={{ duration: 0.6, delay: index * 0.12 + 0.4 }}
                            viewport={{ once: true }}
                            className="flex-shrink-0 flex items-center pt-8"
                          >
                            <svg width="60" height="24" viewBox="0 0 60 24" className="text-accent">
                              <defs>
                                <linearGradient id={`arrow-gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#00CFFF" />
                                  <stop offset="100%" stopColor="#0FFFA0" />
                                </linearGradient>
                              </defs>
                              {/* Arrow Line */}
                              <path
                                d="M 0 12 L 50 12"
                                stroke={`url(#arrow-gradient-${index})`}
                                strokeWidth="2"
                                fill="none"
                                strokeDasharray="4 4"
                              />
                              {/* Arrow Head */}
                              <path
                                d="M 45 8 L 55 12 L 45 16"
                                stroke={`url(#arrow-gradient-${index})`}
                                strokeWidth="2"
                                fill="none"
                                strokeLinejoin="round"
                                strokeLinecap="round"
                              />
                            </svg>
                          </motion.div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Tablet Flow - 2 Columns */}
              <div className="hidden md:grid lg:hidden grid-cols-2 gap-8">
                {[
                  { num: '01', title: 'Data Input', desc: 'RNA-seq data upload and preprocessing', icon: FileUp },
                  { num: '02', title: 'DEG Analysis', desc: 'Differential gene expression processing', icon: Microscope },
                  { num: '03', title: 'ML Processing', desc: 'Machine learning pattern recognition', icon: Brain },
                  { num: '04', title: 'NLP Analysis', desc: 'Natural language processing of findings', icon: FileText },
                  { num: '05', title: 'Visualization', desc: 'Interactive data visualization outputs', icon: BarChart3 }
                ].map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <motion.div
                      key={step.num}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className={`flex flex-col items-center ${index === 4 ? 'col-span-2' : ''}`}
                    >
                      {/* Circular Icon Container */}
                      <div className="relative mb-5">
                        <motion.div
                          animate={{
                            scale: [1, 1.12, 1],
                            opacity: [0.4, 0.7, 0.4]
                          }}
                          transition={{
                            duration: 2.5,
                            repeat: Infinity,
                            delay: index * 0.2
                          }}
                          className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/40 to-secondary/40 blur-xl"
                        />

                        <div className="relative w-18 h-18 rounded-full bg-gradient-to-br from-accent/10 to-secondary/10 backdrop-blur-sm border-2 border-accent/30 flex items-center justify-center">
                          <Icon className="w-8 h-8 text-accent" />
                        </div>

                        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center text-xs font-bold text-white shadow-lg neon-glow">
                          {step.num}
                        </div>
                      </div>

                      <h3 className="text-center font-semibold text-white mb-2">
                        {step.title}
                      </h3>

                      <p className="text-center text-sm text-muted-foreground leading-relaxed max-w-xs">
                        {step.desc}
                      </p>
                    </motion.div>
                  );
                })}
              </div>

              {/* Mobile Flow - Vertical */}
              <div className="md:hidden space-y-6">
                {[
                  { num: '01', title: 'Data Input', desc: 'RNA-seq data upload and preprocessing', icon: FileUp },
                  { num: '02', title: 'DEG Analysis', desc: 'Differential gene expression processing', icon: Microscope },
                  { num: '03', title: 'ML Processing', desc: 'Machine learning pattern recognition', icon: Brain },
                  { num: '04', title: 'NLP Analysis', desc: 'Natural language processing of findings', icon: FileText },
                  { num: '05', title: 'Visualization', desc: 'Interactive data visualization outputs', icon: BarChart3 }
                ].map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <React.Fragment key={step.num}>
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.08 }}
                        viewport={{ once: true }}
                        className="flex items-start gap-4"
                      >
                        {/* Circular Icon */}
                        <div className="flex-shrink-0 relative">
                          <motion.div
                            animate={{
                              scale: [1, 1.1, 1],
                              opacity: [0.3, 0.6, 0.3]
                            }}
                            transition={{
                              duration: 2.5,
                              repeat: Infinity,
                              delay: index * 0.2
                            }}
                            className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/40 to-secondary/40 blur-lg"
                          />

                          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-accent/10 to-secondary/10 backdrop-blur-sm border-2 border-accent/30 flex items-center justify-center">
                            <Icon className="w-7 h-7 text-accent" />
                          </div>

                          <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center text-xs font-bold text-white shadow-lg neon-glow">
                            {step.num}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 pt-2">
                          <h3 className="font-semibold text-white mb-1.5">
                            {step.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {step.desc}
                          </p>
                        </div>
                      </motion.div>

                      {/* Vertical Arrow */}
                      {index < 4 && (
                        <motion.div
                          initial={{ opacity: 0, scaleY: 0 }}
                          whileInView={{ opacity: 1, scaleY: 1 }}
                          transition={{ duration: 0.5, delay: index * 0.08 + 0.3 }}
                          viewport={{ once: true }}
                          className="flex justify-center ml-7"
                        >
                          <svg width="24" height="40" viewBox="0 0 24 40">
                            <defs>
                              <linearGradient id={`arrow-mobile-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#00CFFF" />
                                <stop offset="100%" stopColor="#0FFFA0" />
                              </linearGradient>
                            </defs>
                            <path
                              d="M 12 0 L 12 30"
                              stroke={`url(#arrow-mobile-${index})`}
                              strokeWidth="2"
                              fill="none"
                              strokeDasharray="4 4"
                            />
                            <path
                              d="M 8 25 L 12 35 L 16 25"
                              stroke={`url(#arrow-mobile-${index})`}
                              strokeWidth="2"
                              fill="none"
                              strokeLinejoin="round"
                              strokeLinecap="round"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Analytics Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {analyticsFlow.map((process, index) => {
              const Icon = process.icon;
              return (
                <motion.div
                  key={process.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-3xl font-bold text-accent">{process.step}</div>
                      <Icon className="w-6 h-6 text-secondary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-3">{process.title}</h3>
                    <p className="text-muted-foreground text-sm text-center">{process.description}</p>
                  </div>
                  {index < analyticsFlow.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-accent to-secondary"></div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Core Features
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center hidden">
              GeneSeqer combines powerful bioinformatics with an intuitive interface, making advanced RNA-Seq
              analysis accessible to researchers without programming expertise.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="mb-4">
                    <Icon className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-center">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              GeneSeqer is designed around real bottlenecks in life-science and biotech research. Typical ways teams use the platform include.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-accent">{capability.title}</h3>
                <p className="text-muted-foreground text-justify">{capability.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Applications */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Data Security & Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              RNA-seq data is both scientifically valuable and highly sensitive. GeneSeqer is built with security controls that match the expectations of modern research organisations and clinical teams.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {researchAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <h3 className="text-lg font-semibold mb-4 text-accent">{area.title}</h3>
                <p className="text-muted-foreground text-justify">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Platform Benefits
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center">
              For research teams, GeneSeqer is less about shiny dashboards and more about getting to confident biological answers quickly. It turns RNA-seq from a bottleneck into a routine tool that any lab can use.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-accent/20">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3 justify">{benefit.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-15 hidden">
        <ProductTestimonials
          testimonials={geneSeqerTestimonials}
          title="Genomics Research Excellence"
          subtitle="See how researchers transformed their RNA-Seq analysis workflow"
          gradientFrom="#0FFFA0"
          gradientVia="#00CFFF"
          gradientTo="#0FFFA0"
          iconColor="#0FFFA0"
          dotColor="bg-teal-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Accelerate Your Research Today
            </h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">
              Transform your RNA-Seq analysis workflow with GeneSeqer's no-code platform. Remove bioinformatics
              bottlenecks and unlock insights faster with our guided, ML-powered interface built for biologists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-accent to-secondary px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-accent/25 transition-all duration-300"
              >
                Request Platform Access
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Schedule Demo
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { GeneSeqer };